import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { fileUrl } from '@/api';
import { toast } from 'sonner';
import {
  ArrowRight,
  Check,
  Clock,
  HourglassMedium,
  Lightning,
  Copy,
  DownloadSimple,
  FileText,
  FolderOpen,
  LinkSimple,
  PencilSimple,
  ShareNetwork,
  ShieldCheck,
  X,
  CaretLeft,
  CaretRight,
} from '@phosphor-icons/react';

const IN_FLIGHT = new Set(['draft', 'agreement_signed', 'awaiting_quote', 'quote_ready', 'pending_payment', 'active']);
const SERVICE_FLOW = 'service_v2';

const programImageUrl = (value) => {
  if (!value) return null;
  return /^(https?:|data:|blob:|\/)/i.test(value) ? value : fileUrl(value);
};

const ENGAGEMENT_META = {
  draft: { label: 'Agreement waiting', action: 'Accept the agreement to start the engagement.', tone: 'warning' },
  agreement_signed: { label: 'Documents needed', action: 'Upload the requested documents and company details.', tone: 'warning' },
  awaiting_quote: { label: 'Quote in progress', action: 'Your partner is reviewing the brief and preparing a quote.', tone: 'neutral' },
  quote_ready: { label: 'Quote ready', action: 'Review and approve the final quote.', tone: 'warning' },
  pending_payment: { label: 'Payment due', action: 'Complete payment so work can begin.', tone: 'warning' },
  active: { label: 'In progress', action: 'Track delivery and respond if your partner needs anything.', tone: 'success' },
};

const expiryOptions = [
  { label: '1 hour', seconds: 3600 },
  { label: '24 hours', seconds: 86400 },
  { label: '7 days', seconds: 604800 },
];

const fmtBytes = (n) => {
  const value = Number(n || 0);
  if (!value) return '';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const FounderDashboardSideRail = ({ user }) => {
  const navigate = useNavigate();
  const identity = user?._id || user?.id || user?.email || user?.username || 'default';
  const shortcutKey = `founderboard_vault_shortcuts:${identity}`;

  const [programs, setPrograms] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [vaultFiles, setVaultFiles] = useState([]);
  const [vaultStructure, setVaultStructure] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(true);
  const [loadingVault, setLoadingVault] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [shortcutsHydrated, setShortcutsHydrated] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [sharePasscode, setSharePasscode] = useState('');
  const [shareExpiry, setShareExpiry] = useState(86400);
  const [shareRecipient, setShareRecipient] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const [shareCreated, setShareCreated] = useState(null);
  const [engagementIndex, setEngagementIndex] = useState(0);
  const [programIndex, setProgramIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('programs');
  const [slideDirection, setSlideDirection] = useState(1);
  const swipeStart = useRef(null);

  useEffect(() => {
    setProgramIndex((index) => Math.min(index, Math.max(0, programs.length - 1)));
  }, [programs.length]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [programResult, engagementResult, vaultResult, structureResult] = await Promise.allSettled([
        api.get('/startup/programs/discover'),
        api.get('/marketplace-engagements/mine'),
        api.get('/vault'),
        api.get('/vault/structure'),
      ]);
      if (cancelled) return;

      if (programResult.status === 'fulfilled') {
        setPrograms((programResult.value.data || []).filter((p) => !p.applied));
      }
      if (engagementResult.status === 'fulfilled') {
        const rows = (engagementResult.value.data.engagements || [])
          .filter((e) => IN_FLIGHT.has(e.status))
          .sort((a, b) => String(b.updated_at || b.created_at || '').localeCompare(String(a.updated_at || a.created_at || '')));
        setEngagements(rows);
      }
      setLoadingFeed(false);

      if (vaultResult.status === 'fulfilled') setVaultFiles(vaultResult.value.data.files || []);
      if (structureResult.status === 'fulfilled') setVaultStructure(structureResult.value.data.datarooms || []);
      setLoadingVault(false);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!engagements.length) {
      setEngagementIndex(0);
      return;
    }
    setEngagementIndex((current) => Math.min(current, engagements.length - 1));
  }, [engagements.length]);

  // Hydrate pinned Vault shortcuts only after we know which user-specific key to read.
  // Previously the empty initial Vault response could wipe localStorage before /vault finished loading.
  useEffect(() => {
    setShortcutsHydrated(false);
    try {
      const stored = JSON.parse(localStorage.getItem(shortcutKey) || '[]');
      setSelectedIds(Array.isArray(stored) ? stored.slice(0, 3) : []);
    } catch {
      setSelectedIds([]);
    }
    setShortcutsHydrated(true);
  }, [shortcutKey]);

  // Persist only after hydration, so the initial empty React state never overwrites saved pins.
  useEffect(() => {
    if (!shortcutsHydrated) return;
    try {
      localStorage.setItem(shortcutKey, JSON.stringify(selectedIds.slice(0, 3)));
    } catch {}
  }, [shortcutKey, selectedIds, shortcutsHydrated]);

  // Validate saved IDs only after Vault data has actually loaded.
  // An empty array while loading is not evidence that saved documents no longer exist.
  useEffect(() => {
    if (loadingVault || !shortcutsHydrated) return;
    const valid = new Set(vaultFiles.map((f) => f.id));
    setSelectedIds((current) => {
      const next = current.filter((id) => valid.has(id)).slice(0, 3);
      if (next.length === current.length && next.every((id, index) => id === current[index])) {
        return current;
      }
      return next;
    });
  }, [vaultFiles, loadingVault, shortcutsHydrated]);

  const selectedFiles = useMemo(
    () => selectedIds.map((id) => vaultFiles.find((f) => f.id === id)).filter(Boolean),
    [selectedIds, vaultFiles]
  );

  const labelsBySlot = useMemo(() => {
    const map = {};
    vaultStructure.forEach((room) => {
      (room.slots || []).forEach((slot) => {
        map[`${room.id}::${slot.id}`] = { room: room.name, slot: slot.name };
      });
    });
    return map;
  }, [vaultStructure]);

  const downloadVaultFile = async (file) => {
    try {
      const r = await api.get(`/files/${file.file_id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename || 'file';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    }
  };

  const openShare = (file) => {
    setShareTarget(file);
    setSharePasscode('');
    setShareExpiry(86400);
    setShareRecipient('');
    setShareCreated(null);
  };

  const createShare = async () => {
    if (!shareTarget) return;
    if (!/^\d{4}$/.test(sharePasscode)) {
      toast.error('Enter a 4-digit passcode');
      return;
    }
    setShareBusy(true);
    try {
      const r = await api.post('/vault/links', {
        dataroom_id: shareTarget.dataroom_id,
        vault_file_ids: [shareTarget.id],
        passcode: sharePasscode,
        expiry_seconds: shareExpiry,
        recipient_label: shareRecipient,
      });
      setShareCreated(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create share link');
    } finally {
      setShareBusy(false);
    }
  };

  const toggleShortcut = (id) => {
    setSelectedIds((current) => {
      if (current.includes(id)) return current.filter((item) => item !== id);
      if (current.length >= 3) {
        toast.error('Choose up to 3 Vault shortcuts');
        return current;
      }
      return [...current, id];
    });
  };

  const engagementHref = (e) => {
    if (e.flow === SERVICE_FLOW) return `/startup/services/request/${e.id}`;
    const base = e.vendor_type === 'credit' ? '/startup/credit' : e.vendor_type === 'agency' ? '/startup/marketing' : '/startup/services';
    return `${base}/engagements`;
  };

  const carouselCount = activeTab === 'programs' ? programs.length : engagements.length;
  const carouselIndex = activeTab === 'programs' ? programIndex : engagementIndex;
  const moveSlide = (direction) => {
    if (carouselCount < 2) return;
    setSlideDirection(direction);
    const update = (index) => (index + direction + carouselCount) % carouselCount;
    if (activeTab === 'programs') setProgramIndex(update);
    else setEngagementIndex(update);
  };

  return (
    <>
    <aside
      className="fb-utility-rail flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-[0_12px_36px_rgba(15,23,42,0.045)] dark:shadow-none xl:h-[660px]"
      data-testid="dashboard-founder-card-stack"
    >
      <section className="fb-dashboard-feed flex min-h-0 flex-[1.9] flex-col p-3.5 sm:p-4" aria-label="Programs and services">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-[13px] font-extrabold uppercase tracking-[0.08em] text-[#0D9488] dark:text-teal-300 sm:text-[14px]">Explore & track</h2>
          <button type="button" onClick={() => navigate(activeTab === 'programs' ? '/startup/programs' : '/startup/services/engagements')} className="inline-flex h-8 shrink-0 items-center gap-1 rounded-lg px-2 text-[9.5px] font-bold text-[#0D9488] transition hover:bg-[#0D9488]/[0.07]">
            View all <ArrowRight size={10} weight="bold" />
          </button>
        </div>
        <div className="fb-feed-tabs" role="tablist" aria-label="Explore and track">
          {[['programs', 'New Programs', programs.length], ['services', 'Active Services', engagements.length]].map(([key, label, count]) => (
            <button key={key} type="button" role="tab" id={`dashboard-feed-tab-${key}`} aria-controls={`dashboard-feed-panel-${key}`} aria-selected={activeTab === key} tabIndex={activeTab === key ? 0 : -1} onClick={() => { setActiveTab(key); setSlideDirection(1); }} onKeyDown={(event) => {
              if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
              event.preventDefault();
              const next = event.key === 'Home' ? 'programs' : event.key === 'End' ? 'services' : key === 'programs' ? 'services' : 'programs';
              setActiveTab(next);
              document.getElementById(`dashboard-feed-tab-${next}`)?.focus();
            }}>
              {label}<span>{loadingFeed ? '…' : count}</span>
            </button>
          ))}
        </div>
        <div key={activeTab} className="fb-feed-panel" role="tabpanel" id={`dashboard-feed-panel-${activeTab}`} aria-labelledby={`dashboard-feed-tab-${activeTab}`} tabIndex={0} onKeyDown={(event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault(); moveSlide(event.key === 'ArrowRight' ? 1 : -1);
        }} onTouchStart={(event) => { swipeStart.current = { x: event.touches[0].clientX, y: event.touches[0].clientY }; }} onTouchEnd={(event) => {
          if (!swipeStart.current) return;
          const dx = event.changedTouches[0].clientX - swipeStart.current.x;
          const dy = event.changedTouches[0].clientY - swipeStart.current.y;
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) moveSlide(dx < 0 ? 1 : -1);
          swipeStart.current = null;
        }}>
        {activeTab === 'programs' ? (<>
        {loadingFeed ? (
          <div className="flex flex-1 flex-col justify-center gap-2">
            <div className="h-[86px] animate-pulse rounded-xl bg-secondary/70" />
            <div className="h-8 animate-pulse rounded-lg bg-secondary/50" />
          </div>
        ) : programs.length ? (
          <div className="flex min-h-0 flex-1 flex-col">
            {(() => {
              const featured = programs[programIndex] || programs[0];
              const deadline = featured.open_until
                ? new Date(featured.open_until).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
                : null;
              return (
                <button
                  type="button"
                  onClick={() => navigate(`/startup/programs?apply=${encodeURIComponent(featured.slug)}`)}
                  key={`program-${featured.id || featured.slug}`} style={{ '--feed-direction': slideDirection }}
                  className="fb-feed-slide fb-program-cover-card"
                  data-entity={featured.entity_type || 'vc'} data-cover={Boolean(featured.cover_image)}
                  data-testid={`dashboard-program-${featured.id}`}
                >
                  {featured.cover_image && <img className="fb-program-cover" src={programImageUrl(featured.cover_image)} alt="" onError={(event) => { event.currentTarget.style.display = 'none'; event.currentTarget.closest('.fb-program-cover-card').dataset.cover = 'false'; }} />}
                  <span className="fb-program-cover-shade" aria-hidden="true" />
                  <div className="fb-program-cover-top">
                    <span>Open program</span>
                    {deadline && <span><Clock size={10} weight="bold" /> {deadline}</span>}
                  </div>
                  <div className="fb-program-cover-content">
                    <div className="fb-program-partner">
                      {featured.fund_logo_url && <img src={programImageUrl(featured.fund_logo_url)} alt="" onError={(event) => { event.currentTarget.style.display = 'none'; }} />}
                      <span>{featured.fund_name || 'FounderBoard partner'}</span>
                    </div>
                    <h3>{featured.name}</h3>
                    {featured.description && <p>{featured.description}</p>}
                  </div>
                  <div className="fb-program-cover-action"><span>Review opportunity</span><ArrowRight size={15} weight="bold" /></div>
                </button>
              );
            })()}

          </div>
        ) : (
          <div className="flex flex-1 items-center gap-3 rounded-xl bg-secondary/45 px-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0D9488]/10 text-[#0D9488]">
              <Check size={15} weight="bold" />
            </span>
            <div className="min-w-0">
              <div className="text-[10.5px] font-bold text-text-primary">Nothing new right now</div>
              <div className="mt-0.5 text-[9px] leading-4 text-text-muted">We’ll surface new programs here as soon as they open.</div>
            </div>
          </div>
        )}
        </>) : (<>
        {loadingFeed ? (
          <div className="flex-1 animate-pulse rounded-xl bg-secondary/60" />
        ) : engagements.length ? (
          <div className="flex min-h-0 flex-1 flex-col">
            {(() => {
              const engagement = engagements[engagementIndex] || engagements[0];
              const meta = ENGAGEMENT_META[engagement.status] || ENGAGEMENT_META.active;
              const itemName = engagement.offering?.catalog_item_name || engagement.vendor_name || 'Service engagement';
              const isActive = engagement.status === 'active';
              const stage = { draft: 0, agreement_signed: 1, awaiting_quote: 2, quote_ready: 2, pending_payment: 2, active: 3 }[engagement.status] ?? 0;
              const actionLabel = { draft: 'Review agreement', agreement_signed: 'Upload documents', awaiting_quote: 'View quote status', quote_ready: 'Review quote', pending_payment: 'Complete payment', active: 'Track service' }[engagement.status] || 'Open engagement';
              const updated = engagement.updated_at && !Number.isNaN(new Date(engagement.updated_at).getTime())
                ? new Date(engagement.updated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : null;
              return (
                <article
                  key={`engagement-${engagement.id}`} style={{ '--feed-direction': slideDirection }}
                  className="fb-feed-slide fb-service-update-card"
                  data-testid={`dashboard-engagement-${engagement.id}`}
                >
                  <div className="fb-service-identity">
                    <div className="fb-service-status-row">
                      <span className={`fb-service-status ${isActive ? 'is-progress' : 'is-pending'}`}>
                        {isActive ? <Lightning size={11} weight="fill" /> : <HourglassMedium size={11} weight="bold" />}{meta.label}
                      </span>
                      {updated && <span className="fb-service-updated">Updated {updated}</span>}
                    </div>
                    <h3>{itemName}</h3>
                    <p>{engagement.vendor_name || 'FounderBoard partner'}</p>
                  </div>
                  {engagement.flow === SERVICE_FLOW && (
                    <ol className="fb-service-stages" aria-label="Service progress">
                      {['Agreement', 'Documents', 'Quote & pay', 'Delivery'].map((label, index) => (
                        <li key={label} className={index < stage ? 'is-complete' : index === stage ? 'is-current' : ''} aria-current={index === stage ? 'step' : undefined}>
                          <span>{index < stage ? <Check size={9} weight="bold" /> : index + 1}</span><small>{label}</small>
                        </li>
                      ))}
                    </ol>
                  )}
                  <div className="fb-service-next-step">
                    <span>{['draft', 'agreement_signed', 'quote_ready', 'pending_payment'].includes(engagement.status) ? 'Your next step' : 'Current update'}</span>
                    <p>{meta.action}</p>
                  </div>
                  <button type="button" className="fb-service-open" onClick={() => navigate(engagementHref(engagement))}>{actionLabel}<ArrowRight size={13} weight="bold" /></button>
                </article>
              );
            })()}


          </div>
        ) : (
          <button
            type="button"
            onClick={() => navigate('/startup/services')}
            className="group flex flex-1 items-center gap-3 rounded-xl bg-secondary/45 px-3 text-left transition hover:bg-secondary/75"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0D9488]/10 text-[#0D9488]">
              <Lightning size={15} weight="fill" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[10.5px] font-bold text-text-primary">No work in progress</div>
              <div className="mt-0.5 text-[9px] leading-4 text-text-muted">Explore services when you need a specialist.</div>
            </div>
            <ArrowRight size={10} weight="bold" className="text-[#0D9488] transition-transform group-hover:translate-x-0.5" />
          </button>
        )}
        </>)}
        </div>
        {!loadingFeed && carouselCount > 0 && (
          <div className="fb-feed-carousel-controls" role="group" aria-label={`${activeTab === 'programs' ? 'Program' : 'Service'} carousel controls`}>
            <button type="button" onClick={() => moveSlide(-1)} disabled={carouselCount < 2} aria-label={activeTab === 'programs' ? 'Previous program' : 'Previous service'}><CaretLeft size={12} weight="bold" /></button>
            <span aria-live="polite" aria-atomic="true">{carouselIndex + 1} / {carouselCount}</span>
            <button type="button" onClick={() => moveSlide(1)} disabled={carouselCount < 2} aria-label={activeTab === 'programs' ? 'Next program' : 'Next service'}><CaretRight size={12} weight="bold" /></button>
          </div>
        )}
      </section>

      <div className="mx-4 h-px shrink-0 bg-border" />

      <section className="flex min-h-0 flex-[1.02] flex-col p-3.5 sm:p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[13px] font-extrabold uppercase tracking-[0.08em] text-[#0D9488] dark:text-teal-300 sm:text-[14px]">Quick access</h2>
          </div>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-[9.5px] font-bold text-[#0D9488] transition hover:bg-[#0D9488]/[0.07]"
          >
            <PencilSimple size={10} weight="bold" /> Edit
          </button>
        </div>

        {loadingVault ? (
          <div className="space-y-2">
            <div className="h-11 animate-pulse rounded-lg bg-secondary/60" />
            <div className="h-11 animate-pulse rounded-lg bg-secondary/60" />
            <div className="h-11 animate-pulse rounded-lg bg-secondary/60" />
          </div>
        ) : selectedFiles.length ? (
          <div className="min-h-0 flex-1 space-y-1">
            {selectedFiles.map((file) => {
              const label = labelsBySlot[`${file.dataroom_id}::${file.slot_id}`];
              return (
                <div
                  key={file.id}
                  className="group flex items-center gap-2.5 rounded-lg px-1.5 py-1.5 transition hover:bg-secondary/55"
                  data-testid={`vault-shortcut-${file.id}`}
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#0D9488]/[0.08] text-[#0D9488] dark:text-teal-300">
                    <FileText size={13} weight="bold" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[10px] font-bold text-text-primary" title={file.filename}>{file.filename || 'Vault document'}</div>
                    <div className="mt-0.5 truncate text-[8.5px] text-text-muted">{label?.room || file.dataroom_id}{fmtBytes(file.size) ? ` · ${fmtBytes(file.size)}` : ''}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-0.5 opacity-70 transition group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={() => downloadVaultFile(file)}
                      className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition hover:bg-[#0D9488]/10 hover:text-[#0D9488]"
                      title="Download"
                      aria-label={`Download ${file.filename}`}
                    ><DownloadSimple size={11} weight="bold" /></button>
                    <button
                      type="button"
                      onClick={() => openShare(file)}
                      className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition hover:bg-[#0D9488]/10 hover:text-[#0D9488]"
                      title="Share"
                      aria-label={`Share ${file.filename}`}
                    ><ShareNetwork size={11} weight="bold" /></button>
                  </div>
                </div>
              );
            })}
            {selectedFiles.length < 3 && (
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="mt-1 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[9px] font-semibold text-text-muted transition hover:bg-secondary/55 hover:text-[#0D9488]"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-md border border-dashed border-border"><span className="-mt-px text-sm leading-none">+</span></span>
                Pin another document
              </button>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="group flex flex-1 items-center gap-3 rounded-xl bg-secondary/45 px-3 text-left transition hover:bg-secondary/75"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0D9488]/10 text-[#0D9488]">
              <FolderOpen size={15} weight="bold" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[10.5px] font-bold text-text-primary">Pin the files you reach for most</div>
              <div className="mt-0.5 text-[9px] leading-4 text-text-muted">Choose up to three documents from your Vault.</div>
            </div>
            <ArrowRight size={10} weight="bold" className="text-[#0D9488] transition-transform group-hover:translate-x-0.5" />
          </button>
        )}
      </section>
    </aside>

      {pickerOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/45 p-4 backdrop-blur-sm" onClick={() => setPickerOpen(false)}>
          <div className="w-full max-w-lg rounded-xl border border-border p-5 shadow-2xl" style={{ backgroundColor: 'var(--card)' }} onClick={(e) => e.stopPropagation()} data-testid="vault-shortcut-picker">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading text-lg font-bold text-text-primary">Choose Vault shortcuts</h3>
                <p className="mt-1 text-[11px] text-text-muted">Select up to 3 documents. Your choices stay pinned on this browser.</p>
              </div>
              <button type="button" onClick={() => setPickerOpen(false)} className="text-text-muted hover:text-text-primary"><X size={16} weight="bold" /></button>
            </div>
            {vaultFiles.length ? (
              <div className="max-h-[390px] space-y-1.5 overflow-y-auto pr-1">
                {vaultFiles.map((file) => {
                  const selected = selectedIds.includes(file.id);
                  const label = labelsBySlot[`${file.dataroom_id}::${file.slot_id}`];
                  const disabled = !selected && selectedIds.length >= 3;
                  return (
                    <button key={file.id} type="button" disabled={disabled} onClick={() => toggleShortcut(file.id)} className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition ${selected ? 'border-[#0D9488]/50 bg-[#0D9488]/[0.06]' : 'border-border hover:bg-[#0D9488]/[0.025]'} ${disabled ? 'cursor-not-allowed opacity-45' : ''}`}>
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${selected ? 'border-[#0D9488] bg-[#0D9488] text-white' : 'border-border text-transparent'}`}><Check size={11} weight="bold" /></span>
                      <FileText size={15} className="shrink-0 text-text-muted" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[11px] font-semibold text-text-primary">{file.filename || 'Vault document'}</div>
                        <div className="mt-0.5 truncate text-[9px] text-text-muted">{label ? `${label.room} · ${label.slot}` : file.dataroom_id}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border p-8 text-center text-[11px] text-text-muted">Your Vault does not have any documents yet.</div>
            )}
            <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
              <span className="text-[10px] font-semibold text-text-muted">{selectedIds.length}/3 selected</span>
              <div className="flex gap-2">
                <button type="button" onClick={() => navigate('/startup/vault')} className="rounded-md border border-border px-3 py-2 text-[10px] font-semibold text-text-secondary hover:text-text-primary">Open Vault</button>
                <button type="button" onClick={() => setPickerOpen(false)} className="rounded-md bg-[#0D9488] px-3 py-2 text-[10px] font-semibold text-white">Done</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {shareTarget && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" onClick={() => setShareTarget(null)}>
          <div className="w-full max-w-md rounded-xl border border-border p-5 shadow-2xl" style={{ backgroundColor: 'var(--card)' }} onClick={(e) => e.stopPropagation()} data-testid="vault-shortcut-share-modal">
            {shareCreated ? (
              <>
                <div className="mb-4 text-center">
                  <span className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-[#0D9488]/10 text-[#0D9488]"><ShieldCheck size={20} weight="fill" /></span>
                  <h3 className="font-heading text-lg font-bold text-text-primary">Secure share ready</h3>
                  <p className="mt-1 text-[10px] text-text-muted">Only <strong className="text-text-primary">{shareTarget.filename}</strong> is available through this link.</p>
                </div>
                <div className="rounded-lg border border-border p-3" style={{ backgroundColor: 'var(--card)' }}>
                  <div className="text-[8px] font-bold uppercase tracking-[0.14em] text-text-muted">Share URL</div>
                  <div className="mt-1 break-all font-mono text-[10px] text-text-secondary">{shareCreated.share_url}</div>
                  <button type="button" onClick={() => { navigator.clipboard?.writeText(shareCreated.share_url); toast.success('Share link copied'); }} className="mt-2 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1.5 text-[9px] font-bold text-text-secondary"><Copy size={10} weight="bold" /> Copy link</button>
                </div>
                <div className="mt-2 rounded-lg border border-border p-3" style={{ backgroundColor: 'var(--card)' }}>
                  <div className="text-[8px] font-bold uppercase tracking-[0.14em] text-text-muted">Passcode</div>
                  <div className="mt-1 font-mono text-xl font-bold tracking-[0.28em] text-[#0D9488]">{sharePasscode}</div>
                </div>
                <button type="button" onClick={() => setShareTarget(null)} className="mt-4 w-full rounded-md bg-[#0D9488] py-2.5 text-[11px] font-bold text-white">Done</button>
              </>
            ) : (
              <>
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <div className="mb-0.5 text-[8px] font-extrabold uppercase tracking-[0.14em] text-[#0D9488]">Quick share</div>
                    <h3 className="font-heading text-lg font-bold text-text-primary">{shareTarget.filename}</h3>
                    <p className="mt-1 text-[10px] text-text-muted">Create a passcode-protected link for this document only.</p>
                  </div>
                  <button type="button" onClick={() => setShareTarget(null)} className="text-text-muted hover:text-text-primary"><X size={16} weight="bold" /></button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-[9px] font-bold uppercase tracking-[0.12em] text-text-muted">4-digit passcode</label>
                    <input value={sharePasscode} onChange={(e) => setSharePasscode(e.target.value.replace(/\D/g, '').slice(0, 4))} inputMode="numeric" maxLength={4} placeholder="4242" className="w-full rounded-lg border border-border px-3 py-2 text-center font-mono text-lg tracking-[0.28em] text-text-primary outline-none focus:border-[#0D9488]" style={{ backgroundColor: 'var(--card)' }} />
                  </div>
                  <div>
                    <label className="mb-1 block text-[9px] font-bold uppercase tracking-[0.12em] text-text-muted">Expires in</label>
                    <div className="grid grid-cols-3 gap-1.5">
                      {expiryOptions.map((option) => <button key={option.seconds} type="button" onClick={() => setShareExpiry(option.seconds)} className={`rounded-md border px-2 py-1.5 text-[9.5px] font-bold ${shareExpiry === option.seconds ? 'border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488]' : 'border-border text-text-muted'}`}>{option.label}</button>)}
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-[9px] font-bold uppercase tracking-[0.12em] text-text-muted">Recipient label <span className="normal-case tracking-normal font-medium">(optional)</span></label>
                    <input value={shareRecipient} onChange={(e) => setShareRecipient(e.target.value)} placeholder="Investor, advisor, partner…" className="w-full rounded-lg border border-border px-3 py-2 text-[11px] text-text-primary outline-none focus:border-[#0D9488]" style={{ backgroundColor: 'var(--card)' }} />
                  </div>
                </div>
                <button type="button" onClick={createShare} disabled={shareBusy} className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-[#0D9488] py-2.5 text-[11px] font-bold text-white disabled:opacity-50"><LinkSimple size={12} weight="bold" /> {shareBusy ? 'Creating…' : 'Create secure link'}</button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FounderDashboardSideRail;
